import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ProgressRing } from "./ui/ProgressRing";
import { ChevronDown } from "lucide-react";

interface GoodDeed {
  id: string;
  text: string;
  day: number;
  timestamp: number;
}

interface TrackerData {
  prayers: Record<number, string[]>;
  quranProgress: Record<number, { pages: number; juz: number }>;
  fastedDays: number[];
  missedFastDays: number[];
  goodDeeds: GoodDeed[];
  dhikrCounts: Record<number, Record<string, number>>;
}

interface RamadanDashboardProps {
  trackerData: TrackerData;
  completedDays: number[];
  currentDay: number;
}

export const RamadanDashboard = ({ trackerData, completedDays, currentDay }: RamadanDashboardProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate individual tracker progress (0-100)
  const salahProgress = (() => {
    const totalPrayers = Object.values(trackerData.prayers).reduce(
      (sum, prayers) => sum + prayers.length, 0
    );
    return Math.min((totalPrayers / 150) * 100, 100);
  })();

  const quranProgress = (() => {
    const totalJuz = Object.values(trackerData.quranProgress).reduce(
      (sum, p) => sum + p.juz, 0
    );
    return Math.min((totalJuz / 30) * 100, 100);
  })();

  const fastingProgress = (trackerData.fastedDays.length / 30) * 100;

  const dhikrProgress = (() => {
    const totalDhikr = Object.values(trackerData.dhikrCounts).reduce((daySum, dayCounts) => {
      return daySum + Object.values(dayCounts).reduce((sum, count) => sum + count, 0);
    }, 0);
    return Math.min((totalDhikr / 12000) * 100, 100);
  })();

  const deedsProgress = Math.min((trackerData.goodDeeds.length / 90) * 100, 100);

  // Overall progress (average of all trackers)
  const overallProgress = Math.round(
    (salahProgress + quranProgress + fastingProgress + dhikrProgress + deedsProgress) / 5
  );

  // Worship indicators data
  const worshipIndicators = [
    { label: "Salah", progress: salahProgress, color: "hsl(152, 60%, 45%)" },
    { label: "Quran", progress: quranProgress, color: "hsl(42, 70%, 50%)" },
    { label: "Fasting", progress: fastingProgress, color: "hsl(280, 60%, 55%)" },
    { label: "Dhikr", progress: dhikrProgress, color: "hsl(170, 60%, 45%)" },
    { label: "Deeds", progress: deedsProgress, color: "hsl(340, 65%, 55%)" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-lg overflow-hidden"
    >
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-accent/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ProgressRing
            progress={overallProgress}
            size={40}
            strokeWidth={4}
            color="hsl(var(--primary))"
            showLabel={false}
          >
            <span className="text-xs font-medium text-foreground">{overallProgress}%</span>
          </ProgressRing>
          <div className="text-left">
            <p className="text-sm font-medium text-foreground">Progress Dashboard</p>
            <p className="text-xs text-muted-foreground">Day {currentDay} of 30</p>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-6">
              {/* Large Overall Progress Ring */}
              <div className="flex justify-center py-4">
                <ProgressRing
                  progress={overallProgress}
                  size={140}
                  strokeWidth={10}
                  color="hsl(var(--primary))"
                  showLabel={false}
                >
                  <div className="flex flex-col items-center justify-center">
                    <span className="text-3xl font-display text-foreground">
                      {overallProgress}%
                    </span>
                    <span className="text-xs text-muted-foreground mt-0.5">
                      Day {currentDay} of 30
                    </span>
                  </div>
                </ProgressRing>
              </div>

              {/* Worship Breakdown Row */}
              <div className="flex justify-between items-center gap-2">
                {worshipIndicators.map((indicator) => (
                  <div key={indicator.label} className="flex flex-col items-center gap-1.5 flex-1">
                    <ProgressRing
                      progress={indicator.progress}
                      size={36}
                      strokeWidth={3}
                      color={indicator.color}
                      showLabel={false}
                    >
                      <span className="text-[9px] font-medium text-foreground">
                        {Math.round(indicator.progress)}%
                      </span>
                    </ProgressRing>
                    <span className="text-[10px] text-muted-foreground">{indicator.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
